import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/features/i18n";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import { useState } from "react";

interface UploadProgressToastProps {
  isVisible: boolean;
  progress: number;
  fileName?: string;
  phase?: "trimming" | "uploading" | "processing" | "done";
  error?: string;
  onDismiss?: () => void;
}

/** Abstand über der unteren Navigationsleiste (BottomNav h-16 + Safe-Area + Abstand) */
const BOTTOM_NAV_OFFSET = "6rem"; // 96px – Reiterleiste nicht überdecken

export function UploadProgressToast({
  isVisible,
  progress,
  fileName,
  phase,
  error,
  onDismiss,
}: UploadProgressToastProps) {
  const { t } = useTranslation("common");
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isVisible) return null;

  const isComplete = progress >= 100 && !error;
  const isError = !!error;
  const label =
    phase === "trimming"
      ? t("uploadProgress.trimming")
      : phase === "processing"
        ? t("uploadProgress.processing")
        : t("uploadProgress.uploading");

  return (
    <div
      className={cn(
        "fixed right-4 z-[9999]",
        "flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur-sm",
        "px-4 py-3 shadow-lg max-w-[400px]",
        isMinimized && !isComplete && !isError
          ? "min-w-0 w-auto"
          : "min-w-[280px]",
        "animate-in slide-in-from-bottom-4 fade-in-0"
      )}
      style={{ bottom: BOTTOM_NAV_OFFSET }}
      role="status"
      aria-live="polite"
      aria-label={
        isError
          ? t("uploadProgress.ariaUploadError")
          : isComplete
            ? t("uploadProgress.ariaUploadComplete")
            : t("uploadProgress.ariaUploadRunning")
      }
    >
      {isError ? (
        <>
          <XCircle className="h-5 w-5 text-destructive flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-destructive">
                {t("uploadProgress.uploadFailed")}
              </span>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="flex-shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={t("close")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {error}
            </p>
          </div>
        </>
      ) : isComplete ? (
        <>
          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {t("uploadProgress.uploadSuccess")}
              </span>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="flex-shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={t("close")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {fileName && (
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {fileName}
              </p>
            )}
          </div>
        </>
      ) : isMinimized ? (
        /* Minimierte Ansicht: kompakt, tippen zum Erweitern */
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className="flex w-full items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm -m-1 p-1"
          aria-label={t("uploadProgress.ariaUploadExpand", {
            percent: Math.round(progress),
          })}
        >
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
          <span className="text-sm font-medium truncate flex-1 min-w-0">
            {label.replace("...", "")} {Math.round(progress)}%
          </span>
          <ChevronUp
            className="h-4 w-4 text-muted-foreground flex-shrink-0"
            aria-hidden
          />
        </button>
      ) : (
        <>
          <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-sm font-medium">{label}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs text-muted-foreground">
                  {Math.round(progress)}%
                </span>
                <button
                  type="button"
                  onClick={() => setIsMinimized(true)}
                  className="rounded-sm p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={t("uploadProgress.minimize")}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
            <Progress value={progress} className="h-1.5" />
            <p className="text-xs font-bold text-red-500 mt-1.5">
              {t("uploadProgress.keepAppOpen")}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
